import { useState, useEffect, useCallback, RefObject } from 'react'

type DropdownDimensions = {
  width: number
  height: number
  top: number
  left: number
}

/**
 * Calculates the dropdown dimensions based on a reference element.
 *
 * @param ref - A ref to the element that triggers the dropdown (usually the input container)
 * @param isOpen - Whether the dropdown is currently open
 * @param dropDownPosition - 'under' or 'above'
 *
 * @returns The calculated dimensions or undefined if not applicable
 */
function useDropdownPosition(
  ref: RefObject<HTMLElement>,
  isOpen: boolean,
  dropDownPosition: 'under' | 'above'
): DropdownDimensions | undefined {
  const [dropdownDimensions, setDropdownDimensions] = useState<DropdownDimensions>()

  const updateDimensions = useCallback(() => {
    if (!ref.current) return

    const rect = ref.current.getBoundingClientRect()
    const scrollTop = window.scrollY || document.documentElement.scrollTop
    const scrollLeft = window.scrollX || document.documentElement.scrollLeft

    // Calculate position for the dropdown when displayed above
    const top = rect.top + scrollTop + rect.height
    let left = rect.left + scrollLeft

    // Ensure dropdown stays within viewport bounds
    const viewportWidth = window.innerWidth
    const dropdownWidth = rect.width
    const minLeft = scrollLeft
    const maxLeft = scrollLeft + viewportWidth - dropdownWidth

    // Adjust left position if dropdown would go outside viewport
    if (left < minLeft) left = minLeft
    else if (left > maxLeft) left = maxLeft

    setDropdownDimensions({ width: rect.width, height: rect.height, top, left })
  }, [ref])

  useEffect(() => {
    if (isOpen && dropDownPosition === 'above') {
      updateDimensions()
      // Update dimensions on scroll and resize
      window.addEventListener('scroll', updateDimensions, true)
      window.addEventListener('resize', updateDimensions, true)
      return () => {
        window.removeEventListener('scroll', updateDimensions, true)
        window.removeEventListener('resize', updateDimensions, true)
      }
    }
  }, [isOpen, dropDownPosition, updateDimensions])

  return dropdownDimensions
}

export default useDropdownPosition
